<template>
  <ModalComponent v-model:dialogVisible="editComponent">
    <template #modalContent>
      <div
        class="p-8 relative portrait:p-4 dark:bg-gray-900 dark:text-gray-100"
      >
        <!-- Modal Content -->
        <div
          class="w-full flex flex-col items-start justify-center p-6 rounded-lg shadow-md bg-white dark:bg-gray-800 border border-blue-gray-200 dark:border-gray-700"
        >
          <!-- Modal Header -->
          <div class="w-full flex justify-between items-center mb-6">
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-200">
              Edit Product Collection
            </p>
            <!-- Close Button -->
            <button
              @click="editComponent = false"
              class="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              aria-label="Close modal"
            >
              <i class="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>

          <!-- Collection Name -->
          <div class="w-full mb-6">
            <label
              class="block mb-2 font-medium text-gray-900 dark:text-gray-200"
            >
              Collection Name
            </label>
            <input
              type="text"
              :value="editCollection?.name"
              placeholder="Collection 1"
              :class="[
                'w-full px-4 py-3 rounded-lg border border-blue-gray-200 dark:border-gray-600 bg-transparent text-gray-900 dark:text-gray-200 placeholder-gray-500 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:outline-none transition-all',
                editCollectionNameError
                  ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/10'
                  : '',
              ]"
              aria-invalid="editCollectionNameError"
              aria-describedby="name-error"
            />
            <p
              v-if="editCollectionNameError"
              id="name-error"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              Please provide a valid collection name.
            </p>
          </div>

          <!-- Collection Description -->
          <div class="w-full mb-6">
            <label
              class="block mb-2 font-medium text-gray-900 dark:text-gray-200"
            >
              Collection Description
            </label>
            <textarea
              :value="editCollection?.description"
              placeholder="We sell a lot of things"
              :class="[
                'w-full px-4 py-3 rounded-lg border border-blue-gray-200 dark:border-gray-600 bg-transparent text-gray-900 dark:text-gray-200 placeholder-gray-500 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:outline-none transition-all',
                editCollectionDescriptionError
                  ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/10'
                  : '',
              ]"
              aria-invalid="editCollectionDescriptionError"
              aria-describedby="description-error"
            ></textarea>
            <p
              v-if="editCollectionDescriptionError"
              id="description-error"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              Please provide a valid description.
            </p>
          </div>

          <!-- Add Products -->
          <div class="w-full mb-6">
            <label
              class="block mb-2 font-medium text-gray-900 dark:text-gray-200"
            >
              Products
            </label>
            <span class="text-gray-400 text-xs"
              >You cant add or remove products</span
            >
            <div class="relative">
              <div
                @click="toggleCollectionProducts"
                class="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors max-w-[76vw]"
                aria-haspopup="listbox"
                :aria-expanded="isActive"
              >
                <span class="truncate">
                  {{
                    editCollection?.products.length > 0
                      ? editCollection.products
                          .map((product) => product.name)
                          .join(", ")
                      : "Select Product"
                  }}
                </span>
                <i
                  :class="[
                    'fa-solid fa-chevron-down transform transition-transform duration-300',
                    isActive ? 'rotate-180' : '',
                  ]"
                ></i>
              </div>
              <ul
                v-if="isActive"
                class="absolute z-10 w-full mt-2 max-h-72 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
              >
                <li
                  v-for="option in editCollection?.products"
                  :key="option.id"
                  class="flex items-center px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <input
                    type="checkbox"
                    :id="option.id"
                    :value="option"
                    class="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400"
                  />
                  <label
                    :for="option.id"
                    class="ml-3 text-sm text-gray-800 dark:text-gray-200"
                  >
                    {{ option.name }}
                  </label>
                </li>
              </ul>
            </div>
          </div>

          <!-- Collection Banner -->
          <div class="w-full mb-6">
            <label
              class="block mb-2 font-medium text-gray-900 dark:text-gray-200"
            >
              Collection Banner
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
              <i
                class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-900"
              ></i>
              Max file size: 5MB
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-if="editCollection?.banner"
                class="relative w-20 h-20 rounded-lg border-dashed border-main-800 flex items-center justify-center overflow-hidden"
              >
                <img
                  :src="getFileUrl(editCollection?.banner)"
                  @error="
                    (event) => (event.target.src = '/landscape-placeholder.svg')
                  "
                  class="w-full h-full object-cover rounded-lg"
                  alt="Collection Banner"
                />
                <button
                  @click="removeBanner"
                  class="absolute top-1 right-1 p-1 bg-white/80 rounded-full text-main-900 hover:bg-gray-800/20 transition"
                  aria-label="Remove banner"
                >
                  <i class="fa-solid fa-trash text-sm"></i>
                </button>
              </div>
              <label
                v-else
                class="w-20 h-20 rounded-lg border-dashed border-gray-400 flex items-center justify-center cursor-pointer hover:border-gray-500 transition"
              >
                <i class="fa-solid fa-plus text-gray-400"></i>
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleCollectionImageUpload"
                />
              </label>
            </div>
            <div
              v-if="exceedImageFileSize || invalidFileType"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              <p v-if="exceedImageFileSize">
                Maximum file size: <strong>5MB</strong>
              </p>
              <p v-if="invalidFileType">
                Allowed file types: <strong>jpg, png</strong>
              </p>
            </div>
          </div>

          <!-- Save Button -->
          <div class="w-full flex justify-end gap-3 mt-6">
            <button
              @click="deleteCollection(editCollection?.id)"
              class="px-6 py-3 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition"
            >
              Delete</button
            ><button
              @click="updateCollections"
              class="px-6 py-3 rounded-lg bg-sub-600 text-white font-semibold hover:bg-sub-700 focus:outline-none focus:ring-2 focus:ring-sub-500 transition"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
  <div :key="refreshKey" class="flex flex-col gap-5">
    <div
      class="relative flex flex-col w-full h-min text-main-700 bg-white rounded-lg bg-clip-border p-4 gap-5 dark:bg-gray-800 dark:text-gray-200"
    >
      <!-- Navigation -->
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
      >
        <!-- Back Button and Title -->
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700/50 dark:hover:bg-gray-700/70"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <h3 class="text-[20px] font-medium dark:text-gray-200">Products</h3>
        </div>

        <!-- Add Product and Boost Products Buttons -->
        <div
          class="flex flex-row justify-end items-center gap-4 portrait:w-full"
        >
          <router-link
            to="/add-product"
            class="portrait:w-full align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-3 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-800 dark:hover:bg-sub-900"
          >
            Add Product
          </router-link>
          <button
            disabled
            class="portrait:w-full rounded-lg border border-main-900 py-3 px-4 text-center portrait:text-sm align-middle text-base font-semibold text-main-900 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:border-gray-400 dark:text-gray-200"
          >
            Boost Products
          </button>
        </div>
      </nav>

      <!-- Stats Cards -->
      <div class="flex flex-wrap w-full">
        <!-- Product Sold -->
        <div class="min-w-[120px] md:w-1/2 w-full xl:w-1/3 p-1">
          <div
            class="w-full p-3 bg-red-800/10 hover:bg-red-800/20 rounded-lg cursor-pointer h-full flex justify-between items-center dark:bg-red-900/20 dark:hover:bg-red-900/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">
                {{ sold || 0 }}
              </h3>
              <p
                class="text-main-800 text-xs mt-1 font-normal dark:text-gray-300"
              >
                Product Sold
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-red-800 dark:text-red-400"
            >
              <i class="fa-solid fa-tags text-[20px]"></i>
            </div>
          </div>
        </div>

        <!-- Out of Stock -->
        <div class="min-w-[120px] md:w-1/2 w-full xl:w-1/3 p-1">
          <div
            class="flex justify-between items-center w-full h-full bg-yellow-800/10 hover:bg-yellow-800/20 rounded-lg cursor-pointer p-3 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">
                {{ outofstock }}
              </h3>
              <p
                class="text-main-800 text-xs mt-1 font-normal dark:text-gray-300"
              >
                Products out of stock
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-yellow-800 dark:text-yellow-400"
            >
              <i class="fa-solid fa-tags text-[20px]"></i>
            </div>
          </div>
        </div>

        <!-- In Stock -->
        <div class="min-w-[120px] md:w-1/2 w-full xl:w-1/3 p-1">
          <div
            class="flex justify-between items-center w-full h-full p-3 bg-blue-800/10 hover:bg-blue-800/20 rounded-lg cursor-pointer dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">
                {{ instock }}
              </h3>
              <p
                class="text-main-800 text-xs mt-1 font-normal dark:text-gray-300"
              >
                Products in stock
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-blue-800 dark:text-blue-400"
            >
              <i class="fa-solid fa-tags text-[20px]"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="relative flex flex-col w-full h-full text-main-700 bg-white shadow-xl rounded-lg bg-clip-border p-4 gap-5 dark:bg-gray-800 dark:text-gray-200"
    >
      <!-- Tabs and Search Bar -->
      <div
        class="w-full flex flex-row portrait:flex-col portrait:gap-5 justify-between items-center mt-1 landscape:pl-3"
      >
        <!-- Tabs -->
        <div
          class="relative flex flex-row justify-end items-center gap-4 bg-blue-gray-50/60 rounded-lg dark:bg-gray-700/50"
        >
          <span
            class="absolute inset-0 w-[calc(50%-8px)] h-full bg-button rounded-lg transition-all duration-300 dark:bg-sub-900"
            :class="{
              'left-0': productTab,
              'left-[calc(50%+4px)]': !productTab,
            }"
          ></span>
          <button
            @click="toggleProductTab"
            class="relative z-10 align-middle text-base portrait:text-sm py-2 px-4 rounded-lg font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none bg-transparent"
            :class="{
              'text-white': productTab,
              'text-gray-800 dark:text-gray-200': !productTab,
            }"
          >
            Products
          </button>
          <button
            @click="toggleCollectionTab"
            class="relative z-10 align-middle text-base portrait:text-sm py-2 px-4 rounded-lg font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none bg-transparent"
            :class="{
              'text-white': !productTab,
              'text-gray-800 dark:text-gray-200': productTab,
            }"
          >
            Collections
          </button>
        </div>

        <!-- Search Bar -->
        <div class="mx-3 portrait:w-full">
          <div class="w-full landscape:max-w-sm min-w-[200px] relative">
            <div class="relative w-full">
              <input
                type="text"
                placeholder="Search for product"
                v-model="searchQuery"
                class="peer pl-3 outline outline-0 focus:outline-0 pr-11 dark:bg-gray-700 dark:placeholder-gray-400 py-3 h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-500 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-500 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700"
              />
              <span
                class="absolute h-8 w-8 right-1.5 top-1.5 my-auto px-2 flex items-center bg-main-800 rounded-lg hover:bg-gray-700 dark:bg-sub-800 dark:hover:bg-sub-900"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="#FFF"
                  class="w-8 h-8"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons (Conditionally Rendered) -->
      <div
        v-if="hasCheckedItems"
        class="flex flex-row justify-start items-center gap-4 w-full landscape:pl-3"
      >
        <button
          @click="removeSelectedProducts"
          class="relative z-10 align-middle text-base portrait:text-sm py-2 px-4 rounded-lg font-medium text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-gray-800 bg-red-50 hover:bg-blue-gray-500/20 dark:text-gray-200 dark:bg-red-900/20 dark:hover:bg-red-900/30"
        >
          <i class="fa-solid fa-trash text-red-800 dark:text-red-400"></i>
          Delete
          <span class="text-red-800 dark:text-red-400 text-sm"
            >({{ selectedItemsCount }})</span
          >
        </button>
        <button
          @click="cropImage = true"
          class="relative z-10 align-middle text-base portrait:text-sm py-2 px-4 rounded-lg font-medium text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-gray-800 bg-sub-50 hover:bg-blue-gray-500/20 gap-2 flex justify-between items-center dark:text-gray-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
        >
          <i class="fa-solid fa-rocket text-sub-900 dark:text-blue-400"></i>
          Boost
          <span class="text-sub-900 dark:text-blue-400 text-sm"
            >({{ selectedItemsCount }})</span
          >
        </button>
      </div>

      <!-- Products Table -->
      <div
        v-if="productTab"
        class="relative flex flex-col w-full h-full text-gray-700 bg-white rounded-lg bg-clip-border border border-blue-gray-100 overflow-auto dark:bg-gray-800 dark:border-gray-700"
      >
        <table class="w-full text-left table-auto">
          <thead>
            <tr class="bg-blue-gray-50/50 dark:bg-gray-700/50">
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                <div class="flex items-center gap-3">
                  <label
                    class="relative flex items-center rounded-full cursor-pointer"
                    htmlFor="link"
                  >
                    <input
                      type="checkbox"
                      id="select-all"
                      v-model="isSelectAllChecked"
                      @change="toggleSelectAll"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-400 dark:checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:before:bg-blue-600"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <p
                    class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
                  >
                    Product
                  </p>
                </div>
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                Image
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200 whitespace-nowrap"
              >
                In Stock
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                Price
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                Status
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              ></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredProducts.length === 0">
              <td
                colspan="4"
                class="text-center text-gray-500 py-5 dark:text-gray-400"
              >
                No products added
              </td>
            </tr>
            <tr
              v-else
              v-for="product in filteredProducts"
              :key="product.id"
              class="hover:bg-blue-gray-50/10 dark:hover:bg-gray-700/20 cursor-pointer transition-colors duration-200"
            >
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <div class="flex items-center gap-5">
                  <label
                    class="relative w-max flex items-center rounded-full cursor-pointer"
                    htmlFor="link"
                  >
                    <input
                      type="checkbox"
                      :value="product.id"
                      v-model="selectedProducts"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-400 dark:checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:before:bg-blue-600"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <RouterLink
                    :to="`/edit-product/${product.id}`"
                    class="text-start underline underline-offset-2 block font-medium text-base w-full text-gray-800/80 dark:text-gray-200"
                  >
                    {{ product.name }}
                  </RouterLink>
                </div>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <div class="w-16 h-16 mx-auto">
                  <img
                    :src="getFileUrl(product.image)"
                    @error="
                      (event) =>
                        (event.target.src = '/landscape-placeholder.svg')
                    "
                    :alt="product.name"
                    class="h-full aspect-square object-cover rounded"
                  />
                </div>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ product.stock_quantity }}
                </p>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ getCurrencySymbol(storeCurrencyId) }}{{ product.cost }}
                </p>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <div class="w-max mx-auto">
                  <div
                    :class="[
                      'relative grid items-center px-2 py-1 text-xs font-bold uppercase rounded-xl select-none whitespace-nowrap',
                      product.visibility === 1
                        ? 'text-green-900 bg-green-50 dark:text-green-200 dark:bg-green-900/20'
                        : 'text-red-900 bg-red-50 dark:text-red-200 dark:bg-red-900/20',
                    ]"
                  >
                    <span>{{
                      product.visibility === 1 ? "Visible" : "Hidden"
                    }}</span>
                  </div>
                </div>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  @click="removeProduct(product.id)"
                >
                  <i
                    class="fa-solid fa-trash text-[20px] text-main-900 dark:text-blue-400"
                  ></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Collections Table -->
      <div
        v-else
        class="relative flex flex-col w-full h-full text-gray-700 bg-white rounded-lg bg-clip-border border border-blue-gray-100 overflow-auto dark:bg-gray-800 dark:border-gray-700"
      >
        <table class="w-full text-left table-auto">
          <thead>
            <tr class="bg-blue-gray-50/50 dark:bg-gray-700/50">
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                <div class="flex items-center gap-3">
                  <label
                    class="relative flex items-center rounded-full cursor-pointer"
                    htmlFor="link"
                  >
                    <input
                      type="checkbox"
                      v-model="isSelectAllChecked"
                      @change="toggleSelectAll"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-400 dark:checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:before:bg-blue-600"
                      id="link"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <p
                    class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
                  >
                    Collection
                  </p>
                </div>
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              >
                Banner
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200 whitespace-nowrap"
              >
                No. Products
              </th>
              <th
                class="p-4 text-center text-base font-medium leading-none text-gray-800 dark:text-gray-200"
              ></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredCollections.length === 0">
              <td
                colspan="4"
                class="text-center text-gray-500 py-5 dark:text-gray-400"
              >
                No collections added
              </td>
            </tr>
            <tr
              v-else
              v-for="collection in filteredCollections"
              :key="collection.id"
              class="hover:bg-blue-gray-50/10 dark:hover:bg-gray-700/20 cursor-pointer transition-colors duration-200"
            >
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <div class="flex items-center gap-5">
                  <label
                    class="relative w-max flex items-center rounded-full cursor-pointer"
                    htmlFor="link"
                  >
                    <input
                      type="checkbox"
                      :value="collection.id"
                      v-model="selectedCollections"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-400 dark:checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:before:bg-blue-600"
                      id="link"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <button
                    @click="fetchCollection(collection.id)"
                    class="text-start underline underline-offset-2 block font-medium text-base w-full text-gray-800/80 dark:text-gray-200"
                  >
                    {{ collection.name }}
                  </button>
                </div>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <div class="h-16 mx-auto aspect-video">
                  <img
                    :src="getFileUrl(collection.banner)"
                    @error="
                      (event) =>
                        (event.target.src = '/landscape-placeholder.svg')
                    "
                    :alt="collection.name"
                    class="h-full aspect-square object-cover rounded"
                  />
                </div>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ collection.products.length }}
                </p>
              </td>
              <td
                class="p-4 border-b border-gray-200 py-5 text-center dark:border-gray-700"
              >
                <button
                  @click="deleteCollection(collection?.id)"
                  type="button"
                  class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <i
                    class="fa-solid fa-trash text-[20px] text-main-900 dark:text-blue-400 hover:text-red-600"
                  ></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import ModalComponent from "@/components/modalComponent.vue";
import { mapActions } from "vuex";
export default {
  name: "ViewProducts",
  inject: ["getFileUrl"],
  components: {
    ModalComponent,
  },
  props: {
    maxFileSize: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      products: [],
      collections: [],
      productSearchQuery: "",
      collectionSearchQuery: "",
      summary: {
        totalInStock: 0,
        totalOutOfStock: 0,
      },
      productTab: true,
      itemChecked: true,
      isSelectAllChecked: false,
      selectedProducts: [],
      selectedCollections: [],
      currencies: [],
      editComponent: false,
      toggleCollectionProducts: false,
      isActive: false,
      exceedImageFileSize: false,
      invalidFileType: false,
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
      editCollection: {
        name: "",
        description: "",
        products: [],
        banner: "",
      },
    };
  },
  computed: {
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
    instock() {
      return this.summary.totalInStock || 0;
    },
    outofstock() {
      return this.summary.totalOutOfStock || 0;
    },
    sold() {
      return this.summary.totalSold || 0;
    },
    hasCheckedItems() {
      return (
        this.selectedProducts.length > 0 || this.selectedCollections.length > 0
      );
    },
    selectedItemsCount() {
      return this.selectedProducts.length + this.selectedCollections.length;
    },
    searchQuery: {
      get() {
        return this.productTab
          ? this.productSearchQuery
          : this.collectionSearchQuery;
      },
      set(value) {
        if (this.productTab) {
          this.productSearchQuery = value;
        } else {
          this.collectionSearchQuery = value;
        }
      },
    },
    filteredProducts() {
      const query = this.productSearchQuery.toLowerCase();

      // Filter by name based on search query
      const filtered = this.products.filter((product) =>
        product.name.toLowerCase().includes(query)
      );

      // Sort the filtered products
      return filtered.sort((a, b) => {
        // Sort by status first (visibility: visible > hidden)
        if (a.visibility !== b.visibility) {
          return b.visibility - a.visibility;
        }

        // Finally, by updatedAt (most recently updated first)
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });
    },

    filteredCollections() {
      const query = this.collectionSearchQuery.toLowerCase();
      return this.collections.filter((collection) =>
        collection.name.toLowerCase().includes(query)
      );
    },
  },
  watch: {
    selectedProducts() {
      console.log("selected", this.selectedProducts);
    },
    isSelectAllChecked() {
      console.log("selected", this.selectedProducts);
    },
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchProducts();
        this.fetchCollections();
        this.getCurrency();
      },
    },
  },
  mounted() {
    this.fetchProducts();
    this.fetchCollections();
    this.getCurrency();
  },
  methods: {
    ...mapActions([
      "getProducts",
      "getCollection",
      "fetchCurrency",
      "deleteProduct",
      "deleteCollectionDB",
    ]),
    async deleteCollection(id) {
      if (!id) {
        toast.warning("There has been an error!");
        console.log("Error: There is no id");
        return;
      }

      // Ask user for confirmation
      const userConfirmed = confirm(
        "Are you sure you want to delete this collection?"
      );
      if (!userConfirmed) {
        toast.info("Deletion canceled.");
        return;
      }

      try {
        // Ensure deleteCollectionDB is an async function that returns a Promise
        const data = await this.deleteCollectionDB(id);

        if (!data?.success) {
          throw new Error(data?.error || "Failed to delete collection!");
        }

        toast.success("Collection deleted successfully!");
        this.editComponent = false;

        // Update UI
        this.collections = this.collections.filter(
          (collection) => collection.id !== id
        );
      } catch (error) {
        toast.error("Failed to delete collection.");
        console.error("Error deleting collection:", error);
      }
    },
    handleCollectionImageUpload(event) {
      const files = event.target.files;
      this.exceedImageFileSize = false;
      this.invalidFileType = false;

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidFileType = true;
          return;
        }

        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > this.maxFileSize) {
          this.exceedImageFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.file = file;
          this.showModal = true;
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    },
    handleCroppedImage(croppedImage) {
      const fileDetails = this.file;
      croppedImage.toBlob((blob) => {
        const croppedFile = new File([blob], fileDetails.name, {
          type: fileDetails.type,
        });
        this.collectionImageUrl = croppedFile;
        this.showModal = false;
      });
    },
    handleCancel() {
      this.imageUrl = "";
      this.file = null;
      this.showModal = false;
    },
    handleCloseDialog() {
      this.collectionImageUrl = this.file;
      this.showModal = false;
    },
    fetchProduct(id) {
      return this.products.find((product) => product.id === id);
    },
    fetchCollection(id) {
      // Find the collection by ID
      this.editCollection = this.collections.find(
        (collection) => collection.id === id
      );

      if (this.editCollection) {
        // Map product IDs to their respective product details
        const mappedProducts = this.editCollection.products.map((productId) =>
          this.fetchProduct(productId)
        );

        // Create a new object with the mapped products
        const mappedCollection = {
          ...this.editCollection,
          products: mappedProducts.filter((product) => product !== undefined), // Filter out undefined products
        };

        // Assign the mapped collection to editCollection
        this.editCollection = mappedCollection;

        this.editComponent = true;
      } else {
        console.error("Collection not found");
      }
    },
    toggleProductTab() {
      this.productTab = true;
      this.selectedProducts = [];
      this.selectedCollections = [];
    },
    toggleCollectionTab() {
      this.productTab = !this.productTab;
      this.selectedProducts = [];
      this.selectedCollections = [];
    },
    toggleSelectAll() {
      if (this.isSelectAllChecked) {
        if (this.productTab) {
          this.selectedProducts = this.filteredProducts.map(
            (product) => product.id
          );
        } else {
          this.selectedCollections = this.filteredCollections.map(
            (collection) => collection.id
          );
        }
      } else {
        this.selectedProducts = [];
        this.selectedCollections = [];
      }
    },
    goback() {
      this.$router.back();
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async fetchProducts() {
      try {
        const response = await this.getProducts();
        if (response.success) {
          this.products = response.products;
          this.summary = response.summary;
        } else {
          toast.error(response.error || "Failed to fetch products");
        }
      } catch (error) {
        toast.error("Error fetching products");
        console.error(error);
      }
    },
    async fetchCollections() {
      try {
        const response = await this.getCollection();
        if (response.success) {
          this.collections = response.collections;
        } else {
          toast.error(response.error || "Failed to fetch collections");
        }
      } catch (error) {
        toast.error("Error fetching collections");
        console.error(error);
      }
    },
    // Delete a single product
    async removeProduct(productId) {
      try {
        const deleteProductPromise = new Promise((resolve, reject) => {
          const confirmed = window.confirm(
            "Are you sure you want to delete this product?"
          );

          if (!confirmed) {
            reject(new Error("Delete action cancelled"));
            return;
          }

          // Perform deletion
          this.deleteProduct(productId)
            .then((result) => {
              if (result.success) {
                resolve("Product deleted successfully!");
              } else {
                reject(new Error("Failed to delete product!"));
              }
            })
            .catch((error) => reject(error));
        });

        await toast.promise(
          deleteProductPromise,
          {
            pending: "Processing your request...",
            success: {
              render({ data }) {
                return data || "Operation successful!";
              },
              icon: true,
            },
            error: {
              render({ data }) {
                return data.message || "An error occurred!";
              },
            },
          },
          {
            autoClose: 3000,
            position: "top-right",
          }
        );

        await this.fetchProducts(); // Ensure products are re-fetched only after deletion
        console.log("Product successfully deleted!");
      } catch (error) {
        console.error("Error during deletion:", error.message);
      }
    },
    // Delete all selected products
    async removeSelectedProducts() {
      try {
        if (this.selectedProducts.length === 0) {
          toast.error("No products selected for deletion!");
          return;
        }

        const confirmed = window.confirm(
          `Are you sure you want to delete ${this.selectedProducts.length} selected products?`
        );

        if (!confirmed) {
          throw new Error("Delete action cancelled");
        }

        // Perform bulk deletion
        const deleteAllPromise = (async () => {
          try {
            const deletePromises = this.selectedProducts.map((productId) =>
              this.deleteProduct(productId)
            );

            await Promise.all(deletePromises);
            return `${this.selectedProducts.length} products deleted successfully!`;
          } catch (error) {
            throw new Error("Failed to delete selected products!");
          }
        })();

        await toast.promise(
          deleteAllPromise,
          {
            pending: "Deleting selected products...",
            success: {
              render({ data }) {
                return data || "Operation successful!";
              },
              icon: true,
            },
            error: {
              render({ data }) {
                return data.message || "An error occurred!";
              },
            },
          },
          {
            autoClose: 3000,
            position: "top-right",
          }
        );

        await this.fetchProducts(); // Re-fetch products after deletion
        this.selectedProducts = []; // Clear selected products
        console.log("Selected products successfully deleted!");
      } catch (error) {
        console.error("Error during bulk deletion:", error.message);
      }
    },
  },
};
</script>
